import { AxiosError } from 'axios';
import { ROUTE_ALIASES } from '@/routing/routesAliases';
import { impersonationSessionService } from '@/services/impersonationSession.service';
import { API_ERROR_ACTION, API_FORBIDDEN_REASON, type ApiForbiddenErrorBody } from '@/types/apiError';
import { setAppErrorRedirectPayload } from './appErrorSession';

const isAxiosForbiddenWithAction = (
  error: unknown,
  action: API_ERROR_ACTION
): error is AxiosError<ApiForbiddenErrorBody> =>
  error instanceof AxiosError &&
  error.response?.status === 403 &&
  (error.response.data as ApiForbiddenErrorBody | undefined)?.action === action;

const isAxiosForbiddenRedirect = (error: unknown): error is AxiosError<ApiForbiddenErrorBody> =>
  isAxiosForbiddenWithAction(error, API_ERROR_ACTION.REDIRECT);

/**
 * Handles 403 responses with `action: redirect`: persists payload and navigates to `/error`.
 * Returns true when the error was handled (caller should not show a generic notification).
 */
export const handleForbiddenRedirectError = (error: unknown): boolean => {
  if (isAxiosForbiddenWithAction(error, API_ERROR_ACTION.REDIRECT_TO_SUPERADMIN)) {
    void impersonationSessionService.endSession();
    return true;
  }

  if (!isAxiosForbiddenRedirect(error)) {
    return false;
  }

  const data = error.response?.data;
  const message = typeof data?.message === 'string' ? data.message : '';
  const reason = typeof data?.reason === 'string' ? data.reason : undefined;
  const lockApp = reason === API_FORBIDDEN_REASON.INSUFFICIENT_PERMISSIONS;

  setAppErrorRedirectPayload({
    message,
    reason,
    lockApp,
  });

  if (window.location.pathname === ROUTE_ALIASES.ERROR) {
    return true;
  }

  window.location.replace(`${window.location.origin}${ROUTE_ALIASES.ERROR}`);

  return true;
};

export const isInsufficientPermissionsRedirectError = (error: unknown): boolean => {
  if (!isAxiosForbiddenRedirect(error)) {
    return false;
  }

  const reason = error.response?.data?.reason;
  return reason === API_FORBIDDEN_REASON.INSUFFICIENT_PERMISSIONS;
};

export const isRedirectToSuperadminError = (error: unknown): boolean =>
  isAxiosForbiddenWithAction(error, API_ERROR_ACTION.REDIRECT_TO_SUPERADMIN);
